import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Wrench } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const BASE = '/product-master/snags';
const errText = (e, fallback) => {
  const d = e.response?.data?.detail;
  return typeof d === 'string' ? d : fallback;
};
const pathFor = (kindOrScope) => (['codes', 'a1_blank_code'].includes(kindOrScope) ? 'blank-codes' : 'nominal-yields');

function DryRunResult({ kind, dry, renderItem }) {
  const items = dry.items || [];
  return (
    <>
      <p className="text-slate-600">
        <strong data-testid={`snags-${kind}-proposed-count`}>{items.length}</strong> to fix ·{' '}
        <strong data-testid={`snags-${kind}-blocked-count`}>{dry.blocked_count}</strong> blocked · batch {dry.run_id.slice(0, 8)}
      </p>
      {dry.blocked_reasons ? (
        <ul className="text-slate-500 list-disc pl-4">
          {Object.entries(dry.blocked_reasons).map(([k, v]) => <li key={k}>{v} — {k}</li>)}
        </ul>
      ) : (dry.blocked || []).length > 0 && (
        <ul className="text-slate-500 list-disc pl-4" data-testid={`snags-${kind}-blocked-list`}>
          {dry.blocked.map((b) => <li key={b.product_id}>{b.product_name} — {b.reason}</li>)}
        </ul>
      )}
      <div className="max-h-56 overflow-auto border border-slate-200 rounded bg-white divide-y">
        {items.slice(0, 200).map((it) => (
          <div key={it.yield_id || it.product_id} className="px-2 py-1">{renderItem(it)}</div>
        ))}
      </div>
      {items.length > 200 && <p className="text-slate-400">Showing 200 of {items.length}; apply covers all {items.length}.</p>}
    </>
  );
}

function BatchSection({ kind, title, explain, renderItem, onDone, isAdmin }) {
  const [dry, setDry] = useState(null);
  const [applied, setApplied] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const path = pathFor(kind);

  const call = async (fn, fallback) => {
    setBusy(true);
    try { await fn(); } catch (e) { toast.error(errText(e, fallback)); } finally { setBusy(false); setConfirming(false); }
  };
  const runDry = () => call(async () => {
    const r = await api.post(`${BASE}/${path}/dry-run`);
    setDry(r.data); setApplied(null);
  }, 'Dry run failed');
  const apply = () => call(async () => {
    const r = await api.post(`${BASE}/${path}/apply`, { run_id: dry.run_id });
    setApplied(r.data); toast.success(`Applied ${r.data.applied}, skipped ${r.data.skipped}`); onDone();
  }, 'Apply failed');
  const undo = () => call(async () => {
    const r = await api.post(`${BASE}/${path}/${dry.run_id}/undo`);
    toast.success(`Undone ${r.data.undone}${r.data.kept ? `, kept ${r.data.kept}` : ''}`);
    setApplied(null); setDry(null); onDone();
  }, 'Undo failed');
  const count = dry?.items?.length || 0;

  return (
    <div className="space-y-2 border-t border-slate-200 pt-3" data-testid={`snags-${kind}-section`}>
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-slate-800">{title}</p>
          <p className="text-xs text-slate-500">{explain}</p>
        </div>
        <Button size="sm" variant="outline" onClick={runDry} disabled={busy} data-testid={`snags-${kind}-dry-run`}>
          {busy && !dry ? 'Running…' : 'Dry run'}
        </Button>
      </div>
      {dry && (
        <div className="text-xs space-y-2" data-testid={`snags-${kind}-result`}>
          <DryRunResult kind={kind} dry={dry} renderItem={renderItem} />
          {isAdmin && !applied && count > 0 && (confirming ? (
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={apply} disabled={busy} data-testid={`snags-${kind}-confirm-apply`}>Confirm apply {count}</Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirming(false)} data-testid={`snags-${kind}-cancel-apply`}>Cancel</Button>
            </div>
          ) : (
            <Button size="sm" onClick={() => setConfirming(true)} disabled={busy} data-testid={`snags-${kind}-apply`}>Apply batch</Button>
          ))}
          {applied && (
            <div className="flex items-center gap-2">
              <span className="text-emerald-700" data-testid={`snags-${kind}-applied`}>Applied {applied.applied}, skipped {applied.skipped}</span>
              {isAdmin && <Button size="sm" variant="outline" onClick={undo} disabled={busy} data-testid={`snags-${kind}-undo`}>Undo batch</Button>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RecentBatches({ batches, isAdmin, onDone }) {
  const [busy, setBusy] = useState(null);
  const undoable = (batches || []).filter((b) => ['applied', 'partially_undone'].includes(b.status));
  if (!undoable.length) return null;
  const undo = async (b) => {
    setBusy(b.run_id);
    try {
      const r = await api.post(`${BASE}/${pathFor(b.scope)}/${b.run_id}/undo`);
      toast.success(`Undone ${r.data.undone}${r.data.kept ? `, kept ${r.data.kept}` : ''}`);
      onDone();
    } catch (e) { toast.error(errText(e, 'Undo failed')); } finally { setBusy(null); }
  };
  return (
    <div className="border-t border-slate-200 pt-3 text-xs space-y-1" data-testid="snags-recent-batches">
      <p className="text-sm font-semibold text-slate-800">Applied batches</p>
      {undoable.map((b) => (
        <div key={b.run_id} className="flex items-center justify-between gap-2" data-testid={`snags-batch-${b.run_id}`}>
          <span className="text-slate-600">
            {b.scope === 'a1_blank_code' ? 'Codes' : 'Yields'} · {String(b.applied_at || '').split('T')[0]} · {b.applied_count} applied · {b.status} · {b.run_id.slice(0, 8)}
          </span>
          {isAdmin && (
            <Button size="sm" variant="outline" disabled={busy === b.run_id} onClick={() => undo(b)}
              data-testid={`snags-batch-undo-${b.run_id}`}>Undo</Button>
          )}
        </div>
      ))}
    </div>
  );
}

export function ProductMasterSnagsPanel() {
  const { isAdmin } = useAuth();
  const [ready, setReady] = useState(null);
  const load = () => api.get(`${BASE}/readiness`).then((r) => setReady(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  return (
    <Card className="border-sky-200 bg-sky-50/30" data-testid="product-master-snags-panel">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Wrench className="h-5 w-5 text-sky-700" />
          <CardTitle className="text-base font-semibold text-sky-900">Product Master — Blank codes &amp; nominal yields</CardTitle>
          {ready && (
            <>
              <Badge className="text-xs bg-white text-slate-700 border" data-testid="snags-blank-codes-badge">{ready.blank_codes} blank codes</Badge>
              <Badge className="text-xs bg-white text-slate-700 border" data-testid="snags-duplicate-codes-badge">{ready.duplicate_code_groups} duplicate codes</Badge>
              <Badge className="text-xs bg-white text-slate-700 border" data-testid="snags-derivable-yields-badge">{ready.derivable_blank_nominal_yields} derivable yields</Badge>
              <Badge className="text-xs bg-white text-slate-700 border" data-testid="snags-stale-yields-badge">{ready.stale_derived_yields} stale</Badge>
            </>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <BatchSection
          kind="codes"
          title="Assign missing inventory codes"
          explain="Gives each blank-code product the next free code in its subcategory range. Only the code changes; undo is refused once new records use the product."
          onDone={load}
          isAdmin={isAdmin}
          renderItem={(it) => (
            <span>{it.product_name} <span className="text-slate-400">({it.subcategory_name})</span> → <strong>{it.proposed_code}</strong></span>
          )}
        />
        <BatchSection
          kind="yields"
          title="Fill blank nominal yields from pack ÷ serving"
          explain="Physical sizes only. Observed yield is never written and values already set are never replaced."
          onDone={load}
          isAdmin={isAdmin}
          renderItem={(it) => (
            <span>{it.product_name} · {it.yield_type}: <strong>{Number(it.nominal)}</strong> <span className="text-slate-400">{it.formula}</span></span>
          )}
        />
        <RecentBatches batches={ready?.recent_batches} isAdmin={isAdmin} onDone={load} />
      </CardContent>
    </Card>
  );
}
